import bbox from '@turf/bbox'
import type { Geometry } from 'geojson'
import { featuresToGoogleBoundsLiteral } from './mapBounds'

type Bounds = [[number, number], [number, number]]
type CameraForBounds = {
  cameraForBounds?: (
    bounds: Bounds,
    options?: { padding?: number }
  ) => { zoom?: number } | null | undefined
}
type CameraForBoundsRef = CameraForBounds & {
  getMap?: () => CameraForBounds
}

const DEFAULT_ZOOM = 16
const MIN_ZOOM = 4
const MAX_ZOOM = 18

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max))
}

function getBBox(parcels: { geometry: Geometry }[]) {
  const collection = {
    type: 'FeatureCollection',
    features: parcels.map((parcel) => ({
      type: 'Feature',
      geometry: parcel.geometry,
      properties: {},
    })),
  }

  return bbox(collection)
}

export function calculateIdealZoom(
  mapRef: unknown,
  parcels: { geometry: Geometry }[]
) {
  if (!mapRef || parcels.length === 0) return DEFAULT_ZOOM

  const boundsLiteral = featuresToGoogleBoundsLiteral(parcels)
  if (!boundsLiteral) return DEFAULT_ZOOM

  const bounds: Bounds = [
    [boundsLiteral.west, boundsLiteral.south],
    [boundsLiteral.east, boundsLiteral.north],
  ]

  const typedMapRef = mapRef as CameraForBoundsRef
  const map = typedMapRef.getMap?.() ?? typedMapRef
  const camera = map.cameraForBounds?.(bounds, { padding: 50 })

  if (!camera) {
    const [minLng, minLat, maxLng, maxLat] = getBBox(parcels)
    const span = Math.max(maxLng - minLng, maxLat - minLat)
    const fallbackZoom =
      span > 0 ? Math.floor(Math.log2(360 / span)) : DEFAULT_ZOOM

    return clamp(fallbackZoom, MIN_ZOOM, MAX_ZOOM)
  }

  return clamp(Math.floor(camera.zoom ?? DEFAULT_ZOOM), MIN_ZOOM, MAX_ZOOM)
}
